import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
// import styles from "./portfolioEvolutionChart.module.scss";
import commonStyles from "./../../../finecoPortfolio.common.module.scss";
import colorsStore from "shared/store/colorsStore";
import { utilsFormatDatePortfolioEvolutionChart } from "utils/dateUtils";
import { formatItalianNumber } from "utils/formatUtils";

type PortfolioEvolutionChartProps = {
  data: any;
};

const PortfolioEvolutionChart: React.FC<PortfolioEvolutionChartProps> = ({
  data,
}) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  // Get all values to compute min/max
  const allValues = [
    ...data.map((item: any) => item.avg_return),
    ...data.map((item: any) => item.upper_bound),
    ...data.map((item: any) => item.lower_bound),
  ];

  // Calculate min and max values
  const yMin = Math.min(...allValues);
  const yMax = Math.max(...allValues);

  // Round to nearest 10
  const adjustedMin = Math.floor(yMin / 10) * 10;
  const adjustedMax = Math.ceil(yMax / 10) * 10;

  // Generate ticks every 10 from adjustedMin to adjustedMax
  const yTicks = Array.from(
    { length: (adjustedMax - adjustedMin) / 10 + 1 },
    (_, i) => adjustedMin + i * 10,
  );

  // Calculate X-axis ticks for every 12 months
  const xTicks = data.reduce((acc: string[], item: any, index: number) => {
    if (index % 12 === 0) {
      acc.push(item.date);
    }
    return acc;
  }, []);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className={commonStyles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: "bold" }}>
            {utilsFormatDatePortfolioEvolutionChart(payload[0].payload.date)}
          </div>

          {payload.map((entry: any, index: number) => {
            if (entry.value === undefined) return null;
            return (
              <div key={`value-${index}`}>
                <p
                  style={{
                    color: entry.color,
                    margin: "3px 0",
                    fontSize: "12px",
                  }}
                >
                  {entry.name + ": " + formatItalianNumber(entry.value) + "%"}
                  {/* {parseMonths(entry.payload.month)} */}
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ height: 384, padding: 0, marginTop: 20 }}>
      <ResponsiveContainer width="100%" height="100%" debounce={500}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 45, left: -24, bottom: 5 }}
        >
          <CartesianGrid
            // strokeDasharray="3 3"
            stroke={colorsConfig.FinecoGrayLight1}
            horizontal={true}
          />
          <XAxis
            dataKey="date"
            tickFormatter={utilsFormatDatePortfolioEvolutionChart}
            ticks={xTicks}
            tick={{ fill: colorsConfig.FinecoBlack, fontSize: 12 }}
            dx={20}
            tickMargin={10}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(value) => `${value}%`}
            domain={[adjustedMin, adjustedMax]}
            ticks={yTicks}
            tick={{ fill: colorsConfig.FinecoBlack, fontSize: 12 }}
            tickLine={false}
            interval={0}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="natural"
            dataKey="upper_bound"
            name="Scenario positivo"
            stroke={colorsConfig.Green}
            strokeDasharray="5 5"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
          <Line
            type="natural"
            dataKey="avg_return"
            name="Scenario medio"
            stroke={colorsConfig.orange}
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
          <Line
            type="natural"
            dataKey="lower_bound"
            name="Scenario negativo"
            stroke={colorsConfig.red}
            strokeDasharray="5 5"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PortfolioEvolutionChart;
